import { computed, ref } from 'vue';

// Generic state machine for a multi-step, spotlight-anchored coach-mark tour. Framework for
// the visual piece is `components/tutorial/tutorial-tour.vue`; this composable only owns
// which step is active and calls each step's optional onEnter/onLeave hooks — it knows
// nothing about resume/score/bullet-generator UI, so it can be reused as-is by any future
// tour (e.g. EN-806's shorter "what's new" walkthrough for existing users) by handing it a
// different `steps` array.
//
// Step shape: { target: string (CSS selector), title, body, position?, onEnter?, onLeave? }.
export const useTutorialTour = () => {
	const active = ref(false);
	const stepIndex = ref(0);
	const steps = ref([]);

	let onCloseHandler = null;

	const totalSteps = computed(() => steps.value.length);
	const currentStep = computed(() => steps.value[stepIndex.value] ?? null);
	const isFirstStep = computed(() => stepIndex.value <= 0);
	const isLastStep = computed(() => stepIndex.value >= steps.value.length - 1);

	const runEnter = (index) => {
		steps.value[index]?.onEnter?.();
	};

	const runLeave = (index) => {
		steps.value[index]?.onLeave?.();
	};

	// `onClose` fires exactly once whenever the tour ends, for any reason (Finish on the
	// last step, or an early exit) — the caller's one place to persist a "seen it" flag and
	// run a final safety-net cleanup, independent of whichever per-step onEnter/onLeave
	// hooks did or didn't fire along the way. `startIndex` opens the tour on a later step (a
	// resumed tour); only that step's onEnter runs.
	const start = (resolvedSteps, { onClose, startIndex = 0 } = {}) => {
		if (active.value || !resolvedSteps?.length) {
			return;
		}

		steps.value = resolvedSteps;
		stepIndex.value = startIndex;
		active.value = true;
		onCloseHandler = onClose ?? null;

		runEnter(startIndex);
	};

	const close = () => {
		if (!active.value) {
			return;
		}

		runLeave(stepIndex.value);
		active.value = false;

		const handler = onCloseHandler;
		onCloseHandler = null;
		handler?.();
	};

	const next = () => {
		if (!active.value) {
			return;
		}

		if (isLastStep.value) {
			close();
			return;
		}

		runLeave(stepIndex.value);
		stepIndex.value += 1;
		runEnter(stepIndex.value);
	};

	const previous = () => {
		if (!active.value || isFirstStep.value) {
			return;
		}

		runLeave(stepIndex.value);
		stepIndex.value -= 1;
		runEnter(stepIndex.value);
	};

	const exit = () => close();

	return {
		active,
		stepIndex,
		steps,
		totalSteps,
		currentStep,
		isFirstStep,
		isLastStep,
		start,
		next,
		previous,
		exit,
	};
};
